import React, { useCallback, useMemo } from 'react';

import mobxInjectSelect from '../../common/utils/mobxInjectSelect';

import LocalAdminConsoleView from './LocalAdminConsoleView';
import LocalConsoleToast from './LocalConsoleToast';
import { NO_LOCAL_CONSOLE_ACCESS } from './localConsoleAccess';
import type { LocalConsoleAccess } from './localConsoleAccess';
import { LOCAL_CONSOLE_ROUTES } from './localConsoleRoutes';
import type { LocalConsoleContext } from './types';
import TCClient, { apiEndpoint } from 'client';
import { actions, thunk, useAppDispatch, useAppSelector } from 'redux-stores';

const { setLocalAdminRoute } = actions;

type LocalAdminConsoleProps = Record<string, never>;

type MobxProps = {
  currentOrganization?: { id?: string; name?: string; displayName?: string } | null;
  currentUser?: { displayName?: string } | null;
  currentUserId?: string | null;
  isLocalServer: boolean;
  getLocalConsoleAccessInOrganization: (organizationId?: string) => LocalConsoleAccess;
};

function LocalAdminConsole({
  currentOrganization,
  currentUser,
  currentUserId,
  isLocalServer,
  getLocalConsoleAccessInOrganization,
}: LocalAdminConsoleProps & MobxProps) {
  const dispatch = useAppDispatch();
  const activeRouteId = useAppSelector((state) => state.localAdmin.activeRouteId);
  const isDeploymentUnreachable = useAppSelector(
    (state) => state.offlineMode.isDeploymentUnreachable
  );
  const organizationId = currentOrganization?.id || '';

  const onSelectRoute = useCallback(
    (routeId: string) => {
      dispatch(setLocalAdminRoute(routeId));
    },
    [dispatch]
  );

  const access = currentOrganization?.id
    ? getLocalConsoleAccessInOrganization(currentOrganization.id)
    : NO_LOCAL_CONSOLE_ACCESS;

  const onRetryDeployment = useCallback(() => {
    thunk.fetchOfflineModeActivation(dispatch, organizationId);
  }, [dispatch, organizationId]);

  const context: LocalConsoleContext = useMemo(
    () => ({
      userDisplayName: currentUser?.displayName || '',
      userId: currentUserId || '',
      organizationName: currentOrganization?.displayName || currentOrganization?.name || '',
      organizationId,
      isLocalServer,
      apiBaseUrl:
        (typeof TCClient.config?.baseUrl === 'string' ? TCClient.config.baseUrl : undefined) ||
        apiEndpoint.baseUrl ||
        '',
      deploymentMode: apiEndpoint.mode,
      appVersion: buildInfo?.version || '',
      access,
    }),
    [currentOrganization, currentUser, currentUserId, isLocalServer, organizationId, access]
  );

  return (
    <>
      <LocalAdminConsoleView
        routes={LOCAL_CONSOLE_ROUTES}
        activeRouteId={activeRouteId}
        onSelectRoute={onSelectRoute}
        context={context}
        isDeploymentUnreachable={isDeploymentUnreachable}
        onRetryDeployment={onRetryDeployment}
      />
      <LocalConsoleToast />
    </>
  );
}

export default mobxInjectSelect<LocalAdminConsoleProps, MobxProps>({
  messengerStore: ['currentOrganization', 'isLocalServer', 'getLocalConsoleAccessInOrganization'],
  sessionStore: ['currentUser', 'currentUserId'],
})(LocalAdminConsole);
